import { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
// mui
import { LoadingButton } from "@mui/lab";
import {
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  Stack,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  InputAdornment,
  Box,
  IconButton,
} from "@mui/material";
import { MobileDatePicker } from '@mui/x-date-pickers-pro';
import { alpha } from '@mui/material/styles';
// hooks
import useResponsive from '../../hooks/useResponsive';
// componenets
import Iconify from '../../components/Iconify';
import SvgIconStyle from '../../components/SvgIconStyle';
import Label from '../../components/Label';
// utils
import { fYYYYMMDD } from '../../utils/formatTime';
import axios from '../../utils/axios';


export default function AICareerDialog({ isOpen, closeDialog, career, careerIndex, reload }) {
  const smUp = useResponsive('up', 'sm');
  const { enqueueSnackbar } = useSnackbar();

  const [companyName, setCompanyName] = useState('');
  const [companyNameError, setCompanyNameError] = useState(false);

  const [careerStartDate, setCareerStartDate] = useState('');
  const [noStartDateError, setNoStartDateError] = useState(false);

  const [careerEndDate, setCareerEndDate] = useState('');
  const [noEndDateError, setNoEndDateError] = useState(false);

  const [isCurrent, setIsCurrent] = useState(false);
  const [hidePeriod, setHidePeriod] = useState(false);

  const [careerDescription, setCareerDescription] = useState('');

  const [checkRemove, setCheckRemove] = useState(false);
  const [checkAdd, setCheckAdd] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCompanyName = (e) => {
    setCompanyName(e.target.value);
    setCompanyNameError(!e.target.value);
  };

  const handleIsCurrent = (e) => {
    setCareerEndDate('');
    setIsCurrent(e.target.checked);
  };

  const handleHidePeriod = (e) => {
    setHidePeriod(e.target.checked);
  }

  const handleStartDate = (date) => {
    setCareerStartDate(date);
    setNoStartDateError(!date);
  }

  const handleEndDate = (date) => {
    setCareerEndDate(date);
    setNoEndDateError(!date);
  }

  const close = async () => {
    setCompanyName('');
    setCompanyNameError(false);

    setCareerStartDate('');
    setNoStartDateError(false);

    setCareerEndDate('');
    setNoEndDateError(false);

    setIsCurrent(false);
    setHidePeriod(false);

    setCareerDescription('');

    setCheckRemove(false);
    setCheckAdd(false);

    closeDialog();
  };

  const remove = async () => {
    try {
      setIsSubmitting(true);
      await axios.delete(`/api/crews/ai/career/${career.id}/`);
      reload();
      setIsSubmitting(false);
      setCheckRemove(true);
      enqueueSnackbar('AI 경력 사항이 삭제되었습니다.', { variant: 'error' });
      close();
    } catch (err) {
      setIsSubmitting(false);
      console.error(err);
    }
  }

  const merge = async () => {
    if (!companyName || !careerStartDate || (!isCurrent && !careerEndDate)) {
      setCompanyNameError(!companyName);
      setNoStartDateError(!careerStartDate);
      setNoEndDateError(!careerEndDate && !isCurrent);
      return ;
    }

    try {
      setIsSubmitting(true);
      await axios.post(`/api/crews/ai/career/${career.id}/`, {
        company_name: companyName,
        start_date: fYYYYMMDD(careerStartDate) || careerStartDate,
        end_date: isCurrent ? null : (fYYYYMMDD(careerEndDate) || careerEndDate),
        is_hidden_career_period: hidePeriod,
        description: careerDescription,
      });
      reload();
      setIsSubmitting(false);
      setCheckAdd(true);
    } catch (err) {
      setIsSubmitting(false);
      console.error(err);
    }
  }

  useEffect(() => {
    const initialize = async () => {
      try {
        setCompanyName(career?.company_name || '');
        setCareerDescription(career?.description || '');

        if (career?.start_date !== null) {
          const startDate = new Date(career?.start_date);
          const startDateTZCorrection = new Date(startDate.getTime() + (startDate.getTimezoneOffset() > 0 ? startDate.getTimezoneOffset() * 60000 : 0));
          setCareerStartDate(startDateTZCorrection || '');
        } else {
          setCareerStartDate('');
        }

        if (career?.end_date !== null) {
          const endDate = new Date(career?.end_date);
          const endDateTZCorrection = new Date(endDate.getTime() + (endDate.getTimezoneOffset() > 0 ? endDate.getTimezoneOffset() * 60000 : 0));
          setCareerEndDate(endDateTZCorrection || '');
        } else {
          setCareerEndDate('');
        }
      } catch (err) {
        console.error(err);
      }
    };

    if(isOpen) initialize();
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onClose={close} fullWidth maxWidth={(checkRemove || checkAdd) ? "xs" : "sm"} fullScreen={!checkAdd && !checkRemove && !smUp}>
      {checkAdd && (
      <>
        <DialogContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <SvgIconStyle src={"/icons/icon-check-circle-mono.svg"} sx={{ width: 24, height: 24, color: 'oldprimary.main' }} />
          </Stack>
          <Typography variant={smUp ? "h5" : "subtitle1"} gutterBottom>
            경력 사항이 추가되었어요.
          </Typography>
          <Typography variant={smUp ? "body1" : "body2"}>
            기존에 작성된 경력 사항과 내용이 겹칠 수 있으니, 확인 후 겹치는 내용은 삭제해주세요.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Stack direction={{ xs: "column-reverse", sm: "row" }} sx={{ width: 1 }} spacing={1} justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={close}
              size="large"
              sx={{ px: 4 }}
            >
              경력사항 확인하기
            </Button>
          </Stack>
        </DialogActions>
      </>
      )}
      {checkRemove && (
      <>
        <DialogContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Iconify icon={"eva:alert-triangle-fill"} sx={{ width: 24, height: 24, color: 'error.main' }} />
          </Stack>
          <Typography variant={smUp ? "h5" : "subtitle1"} gutterBottom>
            AI가 작성한 {career?.company_name} 경력을 삭제할까요?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Stack direction={{ xs: "column-reverse", sm: "row" }} sx={{ width: 1 }} spacing={1} justifyContent="flex-end">
            <LoadingButton
              onClick={() => setCheckRemove(false)}
              size="large"
              sx={{ px: 4 }}
              loading={isSubmitting}
            >
              돌아가기
            </LoadingButton>
            <LoadingButton
              variant="contained"
              onClick={remove}
              size="large"
              sx={{ px: 4 }}
              loading={isSubmitting}
            >
              삭제하기
            </LoadingButton>
          </Stack>
        </DialogActions>
      </>
      )}
      {!checkRemove && !checkAdd && (
      <>
      <DialogContent sx={{ p: { xs: 3, sm: 4 } }}>
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
          <Box>
            <Label sx={{ color: 'chart.violet.0', backgroundColor: (theme) => alpha(`${theme.palette.chart['violet'][2]}`, 0.3), px: 1.2, minHeight: 26 }}>
              <SvgIconStyle src={"/icons/ic_ai_generated.svg"} sx={{ width: 16, height: 16 }} /> AI 추천
            </Label>
          </Box>
          <IconButton onClick={close}>
            <Iconify icon={"eva:close-outline"} sx={{ width: 24, height: 24, color: "primary.main" }} />
          </IconButton>
        </Stack>
        <Typography variant='h6' gutterBottom>
          AI가 작성한 경력 사항을 적용해보세요!
        </Typography>
        <Typography variant="body2">
          내용은 수정 가능하며, [기업 전달용 프로필 - 경력 사항]에 추가됩니다.
        </Typography>

        <Stack spacing={1} sx={{ width: 1, mt: 4, mb: 3 }}>
          <Typography variant="subtitle2">
            회사 이름
          </Typography>
          <TextField
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SvgIconStyle src={"/icons/ic_ai_generated.svg"} sx={{ width: 24, height: 24, color: "chart.violet.0" }} />
                </InputAdornment>
              ),
            }}
            sx={{ "& .MuiOutlinedInput-root fieldset": { borderColor: "chart.violet.0" } }}
            fullWidth
            value={companyName}
            error={companyNameError}
            helperText={companyNameError ? '회사이름을 입력해주세요' : ''}
            onChange={handleCompanyName}
          />
        </Stack>
        <Stack direction="row" spacing={2} >
          <Stack spacing={1} sx={{ width: 1 }}>
            <Typography variant="subtitle2">
              시작 연월
            </Typography>
            <MobileDatePicker
              closeOnSelect
              views={['year', 'month']}
              openTo={'year'}
              inputFormat="yyyy년 M월"
              value={careerStartDate}
              onChange={handleStartDate}
              minDate={new Date('1990-01-01')}
              maxDate={careerEndDate || new Date()}
              disableFuture
              renderInput={(params) => (
                <TextField {...params} fullWidth error={noStartDateError} helperText={noStartDateError ? "날짜를 선택해주세요" : "해당 경력의 시작/종료 연월을 입력해주세요."} />
              )}
            />
          </Stack>
          <Stack spacing={1} sx={{ width: 1 }}>
            <Typography variant="subtitle2">
              종료 연월
            </Typography>
            {isCurrent && (
              <TextField fullWidth disabled value="재직중" />
            )}
            {!isCurrent && (
            <MobileDatePicker
              closeOnSelect
              views={['year', 'month']}
              openTo={'year'}
              inputFormat="yyyy년 M월"
              value={careerEndDate}
              minDate={careerStartDate}
              maxDate={new Date()}
              onChange={handleEndDate}
              disableFuture
              renderInput={(params) => (
                <TextField {...params} fullWidth error={noEndDateError} helperText={(noEndDateError && !isCurrent) ? "날짜를 선택해주세요" : ""} />
              )}
              disabled={isCurrent}
            />
            )}
          </Stack>
        </Stack>
        <Stack>
          <FormGroup sx={{ flexDirection: 'row', justifyContent: 'flex-end' }}>
            <FormControlLabel
              control={<Checkbox checked={isCurrent} onClick={handleIsCurrent} />}
              label="현재 이 업무로 근무 중"
              sx={{ color: "text.secondary", '& .MuiFormControlLabel-label' : {fontWeight: 'bold' } }}
            />
          </FormGroup>
          <FormGroup sx={{ flexDirection: 'row', justifyContent: 'flex-end' }}>
            <FormControlLabel
              control={<Checkbox checked={hidePeriod} onClick={handleHidePeriod} />}
              label="재직기간 비공개"
              sx={{ color: "text.secondary", '& .MuiFormControlLabel-label' : {fontWeight: 'bold' } }}
            />
          </FormGroup>
          <Stack spacing={1} sx={{ width: 1, mt: 3 }}>
            <Typography variant="subtitle2">
              경력 설명
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={6}
              value={careerDescription}
              onChange={(e) => setCareerDescription(e.target.value)}
              InputProps={{
                sx: {
                  alignItems: 'baseline',
                },
                startAdornment: (
                  <InputAdornment position="start">
                    <SvgIconStyle src={"/icons/ic_ai_generated.svg"} sx={{ width: 24, height: 24, color: "chart.violet.0" }} />
                  </InputAdornment>
                ),
              }}
              sx={{ "& .MuiOutlinedInput-root fieldset": { borderColor: "chart.violet.0" } }}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ m: { xs: 0, sm: 1 } }}>
        <LoadingButton
          sx={{ px: 4 }}
          onClick={() => setCheckRemove(true)}
          size="large"
          color="error"
          loading={isSubmitting}
        >
          삭제하기
        </LoadingButton>
        <LoadingButton
          sx={{ px: 6 }}
          variant="contained"
          onClick={merge}
          size="large"
          loading={isSubmitting}
        >
          적용하기
        </LoadingButton>
      </DialogActions>
      </>
      )}
    </Dialog>
  )
}